<template>
  <div class="max-w-7xl mx-auto py-8">
    <!-- Page Header -->
    <div class="mb-8">
      <div class="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-8 text-white shadow-xl">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold mb-2">Product Catalogue</h1>
            <p class="text-orange-100 text-lg">Discover our exclusive collection of fine jewellery</p>
          </div>
          <div class="flex space-x-3">
            <router-link 
              to="/customer/cart" 
              class="px-6 py-3 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl hover:bg-opacity-30 transition-all duration-200 text-white"
            >
              View Cart ({{ cartItemCount }})
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Global Filters -->
    <div class="mb-8">
      <GlobalFilters @filter-change="handleFilterChange" />
    </div>

    <!-- Main Content Area -->
    <div class="w-full">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
          <p class="mt-2 text-gray-500">Loading our exclusive collection...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center py-8">
          <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-2">Failed to load collection</h3>
          <p class="text-gray-600 mb-4">{{ error }}</p>
          <button
            @click="loadProducts"
            class="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Try Again
          </button>
        </div>

        <!-- Products Grid -->
        <div v-else>
          <!-- No Products -->
          <div v-if="filteredProducts.length === 0" class="text-center py-8">
            <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-2">No products found</h3>
            <p class="text-gray-600">
              {{ filtersStore.searchQuery || filtersStore.selectedCategory ? 'Try adjusting your search or filters.' : 'Our collection is being curated. Please check back soon.' }}
            </p>
          </div>

          <!-- Products Grid -->
          <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div
              v-for="product in filteredProducts"
              :key="product.id"
              class="group bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-200 cursor-pointer flex flex-col h-full"
            >
              <!-- Product Image -->
                <div 
                  class="aspect-w-1 aspect-h-1 bg-black relative overflow-hidden cursor-pointer"
                  @click="openImageModal(product)"
                >
                <img
                  v-if="product.images && product.images.length > 0"
                  :src="getImageUrl(product.images[0])"
                  :alt="product.name"
                  class="w-full h-48 object-contain hover:scale-110 transition-transform duration-500"
                />
                <div v-else class="w-full h-48 bg-black flex items-center justify-center">
                  <svg class="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
                  </svg>
                </div>
                <!-- Category Badge -->
                <div class="absolute top-2 right-2">
                  <span class="bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-medium">{{ product.category }}</span>
                </div>
                <!-- View Images Overlay -->
                <div v-if="product.images && product.images.length > 0" class="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div class="bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2 text-sm font-medium text-gray-900">
                    <svg class="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View Images
                  </div>
                </div>
              </div>

              <!-- Product Info -->
              <div class="p-4 flex flex-col flex-grow">
                <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ product.name }}</h3>
                <p class="text-gray-600 text-sm mb-3 line-clamp-2">{{ product.description }}</p>
                
                <!-- Labels/Tags -->
                <div v-if="product.labels && product.labels.length > 0" class="mb-3">
                  <div class="flex flex-wrap gap-1">
                    <span v-for="label in product.labels" :key="label.id" :class="[
                      'px-2 py-1 rounded text-xs font-semibold',
                      label.type === 'category' ? 'bg-orange-100 text-orange-700 border border-orange-300' :
                      label.type === 'subcategory' ? 'bg-blue-100 text-blue-700 border border-blue-300' :
                      'bg-amber-100 text-amber-700 border border-amber-300'
                    ]">
                      {{ label.name }}
                    </span>
                  </div>
                </div>

                <!-- Minimal Info (Always Visible) -->
                <div class="space-y-2 mb-3">
                  <div class="flex justify-between text-sm">
                    <span class="text-gray-500">SKU:</span>
                    <span class="font-medium text-gray-900">{{ product.sku }}</span>
                  </div>
                  <div class="flex justify-between text-sm">
                    <span class="text-gray-500">Metal:</span>
                    <span class="font-medium text-gray-900">{{ product.metal || 'N/A' }}</span>
                  </div>
                </div>

                <!-- Expandable Details -->
                <div v-if="expandedProducts.includes(product.id)" class="space-y-2 mb-4 border-t pt-3">
                  <div v-if="product.gross_weight" class="flex justify-between text-sm">
                    <span class="text-gray-500">Gross Weight:</span>
                    <span class="font-medium text-gray-900">{{ product.gross_weight }}g</span>
                  </div>
                  <div v-if="product.stone_weight" class="flex justify-between text-sm">
                    <span class="text-gray-500">Stone Weight:</span>
                    <span class="font-medium text-gray-900">{{ product.stone_weight }}g</span>
                  </div>
                  <div v-if="product.net_weight" class="flex justify-between text-sm">
                    <span class="text-gray-500">Net Weight:</span>
                    <span class="font-medium text-gray-900">{{ product.net_weight }}g</span>
                  </div>
                </div>

                <!-- Expand/Collapse Button -->
                <button
                  @click="toggleProductDetails(product.id)"
                  class="w-full text-orange-600 hover:text-orange-700 text-sm font-medium mb-3 transition-colors flex items-center justify-center"
                >
                  <span>{{ expandedProducts.includes(product.id) ? 'Show Less' : 'Show More Details' }}</span>
                  <svg 
                    :class="['w-4 h-4 ml-1 transition-transform', expandedProducts.includes(product.id) ? 'rotate-180' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <!-- Cart Controls -->
                <div v-if="getCartItem(product.id)" class="space-y-2 mt-auto">
                  <!-- Quantity Controls -->
                  <div class="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                    <button
                      @click="updateQuantity(product.id, -1)"
                      :disabled="getCartItem(product.id).quantity <= 1"
                      class="w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      aria-label="Decrease quantity"
                      title="Decrease quantity"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                      </svg>
                    </button>
                    
                    <span class="text-lg font-semibold text-gray-900 px-4">
                      {{ getCartItem(product.id).quantity }}
                    </span>
                    
                    <button
                      @click="updateQuantity(product.id, 1)"
                      class="w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center hover:bg-orange-700 transition-colors"
                      aria-label="Increase quantity"
                      title="Increase quantity"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </div>
                  
                  <!-- Remove Button -->
                  <button
                    @click="removeFromCart(product.id)"
                    class="w-full bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                  >
                    Remove from Cart
                  </button>
                </div>

                <!-- Add to Cart Button -->
                <button
                  v-else
                  @click="addToCart(product)"
                  class="w-full bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors mt-auto"
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    <!-- Pagination -->
    <div v-if="currentPagination && currentPagination.pages > 1" class="mt-8 flex justify-center">
      <div class="bg-white shadow-lg rounded-xl border border-gray-200 p-4">
        <div class="flex items-center space-x-2">
          <button
            @click="changePage(currentPagination.page - 1)"
            :disabled="currentPagination.page <= 1"
            class="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>
          
          <div class="flex items-center space-x-1">
            <span class="px-4 py-2 text-sm font-medium text-gray-700">
              Page {{ currentPagination.page }} of {{ currentPagination.pages }}
            </span>
            <span class="px-4 py-2 text-sm text-gray-500">
              ({{ currentPagination.total }} total products)
            </span>
          </div>
          
          <button
            @click="changePage(currentPagination.page + 1)"
            :disabled="currentPagination.page >= currentPagination.pages"
            class="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>

    <!-- Image Viewer Modal -->
    <div v-if="showImageModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/80 backdrop-blur-sm" @click="closeImageModal"></div>
      <div class="relative bg-white rounded-2xl shadow-2xl border border-orange-200/20 max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <!-- Modal Header -->
        <div class="flex items-center justify-between p-6 border-b border-orange-200/50">
          <div class="flex items-center">
            <div class="w-10 h-10 mr-3">
              <img 
                src="/logo6.png" 
                alt="Annzz Logo" 
                class="w-full h-full object-contain"
              />
            </div>
            <div>
              <h3 class="text-xl font-bold text-gray-900 font-luxury">{{ selectedProduct?.name }}</h3>
              <p class="text-sm text-gray-600 font-light">Product Images</p>
            </div>
          </div>
          <button 
            @click="closeImageModal"
            class="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200"
            aria-label="Close image viewer"
            title="Close image viewer"
          >
            <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Image Content -->
        <div class="p-6">
          <div v-if="productImages.length > 0" class="space-y-6">
            <!-- Main Image Display -->
            <div class="relative">
              <div class="w-full max-w-3xl mx-auto aspect-w-16 aspect-h-9 bg-black rounded-xl overflow-hidden flex items-center justify-center" style="min-height:360px; max-height:480px;">
                <img 
                  :src="getImageUrl(currentImage)" 
                  :alt="`${selectedProduct?.name} - Image ${currentImageIndex + 1}`"
                  class="w-full h-full object-contain object-center"
                  style="min-height:360px; max-height:480px;"
                  @error="handleImageError"
                />
              </div>
              
              <!-- Navigation Arrows -->
              <button 
                v-if="productImages.length > 1"
                @click="previousImage"
                class="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all duration-200"
              >
                <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <button 
                v-if="productImages.length > 1"
                @click="nextImage"
                class="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all duration-200"
              >
                <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <!-- Image Counter -->
              <div class="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 backdrop-blur-sm rounded-lg px-3 py-1 text-white text-sm font-medium">
                {{ currentImageIndex + 1 }} / {{ productImages.length }}
              </div>
            </div>

            <!-- Thumbnail Navigation -->
            <div v-if="productImages.length > 1" class="flex space-x-2 overflow-x-auto pb-2">
              <button
                v-for="(image, index) in productImages"
                :key="index"
                @click="setCurrentImage(index)"
                :class="[
                  'flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-200',
                  currentImageIndex === index 
                    ? 'border-orange-500 shadow-lg' 
                    : 'border-gray-200 hover:border-orange-300'
                ]"
              >
                <img 
                  :src="getImageUrl(image)" 
                  :alt="`Thumbnail ${index + 1}`"
                  class="w-full h-full object-contain"
                  @error="handleImageError"
                />
              </button>
            </div>
          </div>

          <!-- No Images State -->
          <div v-else class="text-center py-12">
            <div class="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">No Images Available</h3>
            <p class="text-gray-600">This product doesn't have any images yet.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { productsAPI, cartAPI } from '../services/api'
import { useLabelsStore } from '../stores/labels'
import { useFiltersStore } from '../stores/filters'
import GlobalFilters from '../components/GlobalFilters.vue'
import { PAGINATION_LIMITS } from '../constants/pagination'

export default {
  name: 'Catalogue',
  components: {
    GlobalFilters
  },
  setup() {
    // State
    const products = ref([])
    const loading = ref(false)
    const error = ref('')
    const pagination = ref(null)
    const currentPage = ref(1)
    const cartCount = ref(0)
    const cartItems = ref([])

    // Expandable product details state
    const expandedProducts = ref([])

    // Image viewer state
    const showImageModal = ref(false)
    const selectedProduct = ref(null)
    const currentImageIndex = ref(0)

    // Stores
    const labelsStore = useLabelsStore()
    const filtersStore = useFiltersStore()

    // Use products directly from backend (no client-side filtering)
    const filteredProducts = computed(() => {
      return products.value
    })

    // Use pagination from backend
    const currentPagination = computed(() => {
      return pagination.value
    })

    // Methods
    const loadCartItems = async () => {
      try {
        const items = await cartAPI.getCart()
        cartItems.value = items
      } catch (error) {
        console.error('Failed to load cart items:', error)
        cartItems.value = []
      }
    }

    const loadCartCount = async () => {
      try {
        const summary = await cartAPI.getCartSummary()
        cartCount.value = summary.total_items || 0
      } catch (error) {
        console.error('Failed to load cart count:', error)
        cartCount.value = 0
      }
    }

    // Get cart item for a specific product
    const getCartItem = (productId) => {
      return cartItems.value.find(item => item.product_id === productId)
    }

    // Image viewer computed properties
    const productImages = computed(() => {
      if (!selectedProduct.value?.images) return []
      try {
        return Array.isArray(selectedProduct.value.images) 
          ? selectedProduct.value.images 
          : JSON.parse(selectedProduct.value.images)
      } catch {
        return []
      }
    })

    const currentImage = computed(() => {
      if (productImages.value.length === 0) return ''
      return productImages.value[currentImageIndex.value] || productImages.value[0]
    })

    // Methods
    const loadProducts = async () => {
      loading.value = true
      error.value = ''
      
      try {
        const params = {
          page: currentPage.value,
          limit: PAGINATION_LIMITS.PRODUCTS,
          visible: true,
          ...filtersStore.filterParams
        }

        console.log('Loading products with params:', params)
        const response = await productsAPI.getProducts(params)
        console.log('Products response:', response)
        products.value = response.data.products
        pagination.value = response.data.pagination
      } catch (err) {
        console.error('Failed to load products:', err)
        error.value = err.response?.data?.message || 'Failed to load products'
      } finally {
        loading.value = false
      }
    }

    // Watch for filter changes
    watch(() => filtersStore.filterParams, () => {
      currentPage.value = 1
      loadProducts()
    }, { deep: true })

    const handleFilterChange = (filters) => {
      // Filter change is handled by the watcher above
      console.log('Filter change:', filters)
    }

    const changePage = (page) => {
      if (page >= 1 && page <= pagination.value.pages) {
        currentPage.value = page
        loadProducts()
      }
    }

    const addToCart = async (product) => {
      try {
        await cartAPI.addToCart(product, 1)
        // Refresh cart data after adding item
        await loadCartItems()
        await loadCartCount()
        console.log('Added to cart:', product.name)
      } catch (error) {
        console.error('Failed to add to cart:', error)
      }
    }

    const updateQuantity = async (productId, change) => {
      try {
        const cartItem = getCartItem(productId)
        if (!cartItem) return

        const newQuantity = cartItem.quantity + change
        if (newQuantity <= 0) {
          await removeFromCart(productId)
        } else {
          await cartAPI.updateCartItem(cartItem.id, newQuantity)
          await loadCartItems()
          await loadCartCount()
        }
      } catch (error) {
        console.error('Failed to update quantity:', error)
      }
    }

    const removeFromCart = async (productId) => {
      try {
        const cartItem = getCartItem(productId)
        if (!cartItem) return

        await cartAPI.removeFromCart(cartItem.id)
        await loadCartItems()
        await loadCartCount()
        console.log('Removed from cart:', productId)
      } catch (error) {
        console.error('Failed to remove from cart:', error)
      }
    }

    const toggleProductDetails = (productId) => {
      const index = expandedProducts.value.indexOf(productId)
      if (index > -1) {
        expandedProducts.value.splice(index, 1)
      } else {
        expandedProducts.value.push(productId)
      }
    }

    const getImageUrl = (imageName) => {
      // Return placeholder if no image name
      if (!imageName) {
        return 'data:image/svg+xml;base64,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'
      }
      
      // If it's a full URL, return as is
      if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
        return imageName
      }
      
      // Use the nginx proxy for uploads
      const baseUrl = import.meta.env.VITE_API_URL || ''
      const url = `${baseUrl}/uploads/${imageName}`
      return url
    }

    // Image Viewer Methods
    const openImageModal = (product) => {
      selectedProduct.value = product
      currentImageIndex.value = 0
      showImageModal.value = true
    }

    const closeImageModal = () => {
      showImageModal.value = false
      selectedProduct.value = null
      currentImageIndex.value = 0
    }

    const nextImage = () => {
      if (productImages.value.length > 1) {
        currentImageIndex.value = (currentImageIndex.value + 1) % productImages.value.length
      }
    }

    const previousImage = () => {
      if (productImages.value.length > 1) {
        currentImageIndex.value = currentImageIndex.value === 0 
          ? productImages.value.length - 1 
          : currentImageIndex.value - 1
      }
    }

    const setCurrentImage = (index) => {
      currentImageIndex.value = index
    }

    const handleImageError = (event) => {
      // Replace broken image with placeholder
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRkZGRkZGIi8+CjxwYXRoIGQ9Ik0xMDAgNTBDMTIzLjE5NiA1MCAxNDIgNjguODA0IDE0MiA5MkMxNDIgMTE1LjE5NiAxMjMuMTk2IDEzNCAxMDAgMTM0Qzc2LjgwNCAxMzQgNTggMTE1LjE5NiA1OCA5MkM1OCA2OC44MDQgNzYuODA0IDUwIDEwMCA1MFoiIGZpbGw9IiNGRkMxMDciLz4KPHBhdGggZD0iTTEwMCAxNTRDMTIzLjE5NiAxNTQgMTQyIDE3Mi44MDQgMTQyIDE5NkMxNDIgMjE5LjE5NiAxMjMuMTk2IDIzOCAxMDAgMjM4Qzc2LjgwNCAyMzggNTggMjE5LjE5NiA1OCAxOTZDNTggMTcyLjgwNCA3Ni44MDQgMTU0IDEwMCAxNTRaIiBmaWxsPSIjRkZDMTA3Ii8+Cjwvc3ZnPgo='
    }

    // Lifecycle
    onMounted(() => {
      loadProducts()
      loadCartItems()
      loadCartCount()
    })

    return {
      products,
      filteredProducts,
      loading,
      error,
      pagination,
      currentPagination,
      cartItemCount: cartCount,
      cartItems,
      expandedProducts,
      // Image viewer state
      showImageModal,
      selectedProduct,
      currentImageIndex,
      productImages,
      currentImage,
      // Stores
      filtersStore,
      // Methods
      loadProducts,
      loadCartItems,
      loadCartCount,
      handleFilterChange,
      changePage,
      addToCart,
      updateQuantity,
      removeFromCart,
      toggleProductDetails,
      getCartItem,
      getImageUrl,
      // Image viewer methods
      openImageModal,
      closeImageModal,
      nextImage,
      previousImage,
      setCurrentImage,
      handleImageError
    }
  }
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style> 